import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../ui/Modal.jsx';
import Card from '../ui/Card.jsx';
import Skeleton from '../ui/Skeleton.jsx';
import Button from '../ui/Button.jsx';
import PersonRow from '../search/PersonRow';
import ProfileSheet from '../profile/ProfileSheet.jsx';
import { useSkillPeople, fetchSkillPeople } from '../../hooks/useSkillBrowse';
import { trackBrowseEvent } from '../../utils/browseEvents';
import { skillShape } from './SkillBrowseRow.jsx';

/**
 * Ein Skill, aufgeklappt (Skills entdecken, PLAN § 2.1/2.2).
 *
 * Zwei Listen: „Können helfen“ und „Wollen das auch lernen“. Wer den Skill
 * selbst anbietet, sieht zuerst die Lernenden: Für Helfende ist das die
 * interessante Seite, und sie ist die „schwere“ (ANALYSE § 2, Asymmetrie).
 * Bietet niemand den Skill an, steht dort ein Satz statt einer leeren Liste.
 *
 * Ein Tipp auf eine Person öffnet die bekannte Profilkarte als Blatt
 * (`ProfileSheet`, Herkunft `SKILL_BROWSE` für die Vernetzungsanfrage, FF 3).
 *
 * Telefon: Vollbild wie eine eigene Seite (Listen können lang werden, und die
 * Profilkarte legt sich als Blatt darüber). Desktop: zentrierte Karte.
 * Kein `layoutId`-Gleiten vom Listennamen in den Kopf: Das Modal hängt per Portal
 * am <body>, ein sauberer gemeinsamer Übergang wäre nur mit eigenem
 * LayoutGroup-Umbau zu haben. Der Kopf blendet weich ein (Modal-Animation,
 * „Bewegung reduzieren“ schaltet sie ab).
 */

const SECTION = {
  offer: {
    title: 'Können helfen',
    tone: 'bg-offer',
    empty: 'Noch niemand bietet das an.',
  },
  seek: {
    title: 'Wollen das auch lernen',
    tone: 'bg-primary', // kleiner lila Punkt bleibt (Heiko, 16.10.)
    empty: 'Gerade will das niemand sonst lernen.',
  },
};

const PeopleSection = ({ skill, role, count, loader, onOpenPerson, registerRemove }) => {
  const enabled = count > 0;
  const list = useSkillPeople(skill.id, role, { enabled, loader });
  registerRemove(role, list.removePerson);
  const meta = SECTION[role];
  const shownCount = list.total ?? count;
  const headingId = `skill-${skill.id}-${role}`;

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <h3
        id={headingId}
        className="flex items-center gap-2 px-1 text-2xs font-semibold uppercase tracking-[0.08em] text-muted-foreground"
      >
        <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.tone}`} />
        <span>{enabled ? `${meta.title} (${shownCount})` : meta.title}</span>
      </h3>

      {!enabled ? (
        <p className="px-1 text-sm text-muted-foreground">{meta.empty}</p>
      ) : list.status === 'error' ? (
        <div role="status" className="space-y-2 px-1">
          <p className="text-sm text-muted-foreground">Die Liste konnte gerade nicht geladen werden.</p>
          <Button size="sm" variant="secondary" onClick={list.retry}>Noch mal versuchen</Button>
        </div>
      ) : list.status !== 'ready' ? (
        <Card variant="flat" className="divide-y divide-border px-2 py-1" aria-busy="true">
          <span className="sr-only">Wird geladen…</span>
          {Array.from({ length: Math.min(count, 3) }).map((_, index) => <Skeleton.Row key={index} />)}
        </Card>
      ) : list.people.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">{meta.empty}</p>
      ) : (
        <>
          <Card variant="flat" className="divide-y divide-border px-2 py-1">
            {list.people.map(person => (
              <PersonRow
                key={person.userId}
                profile={person}
                onOpen={() => onOpenPerson(person, role)}
              />
            ))}
          </Card>
          {list.nextCursor && (
            <div className="flex flex-col items-center gap-1 pt-1">
              <Button size="md" variant="secondary" onClick={list.loadMore} disabled={list.loadingMore}>
                {list.loadingMore ? 'Lädt…' : 'Weitere laden'}
              </Button>
              {list.loadMoreError && (
                <p role="status" className="text-meta text-muted-foreground">Das hat gerade nicht geklappt.</p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

PeopleSection.propTypes = {
  skill: skillShape.isRequired,
  role: PropTypes.oneOf(['offer', 'seek']).isRequired,
  count: PropTypes.number.isRequired,
  loader: PropTypes.func.isRequired,
  onOpenPerson: PropTypes.func.isRequired,
  registerRemove: PropTypes.func.isRequired,
};

const SkillDetailSheet = ({
  open,
  onClose,
  skill,
  category,
  loader = fetchSkillPeople,
  trackEvents = true,
}) => {
  const [person, setPerson] = useState(null);
  const removers = React.useRef({});
  const registerRemove = (role, fn) => { removers.current[role] = fn; };

  if (!skill) return null;

  const order = skill.me === 'offer' ? ['seek', 'offer'] : ['offer', 'seek'];
  const counts = { offer: skill.offerCount || 0, seek: skill.seekCount || 0 };

  const openPerson = (entry, role) => {
    setPerson({ userId: entry.userId, name: entry.displayName, role });
    if (trackEvents) trackBrowseEvent('BROWSE_PROFILE_OPENED', { skillId: skill.id, role });
  };

  const handleBlocked = () => {
    if (!person) return;
    Object.values(removers.current).forEach(fn => fn && fn(person.userId));
    setPerson(null);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={<span className="block break-words">{skill.name}</span>}
        size="md"
        fullscreenOnMobile
        bodyClassName="px-5 pb-6 pt-1"
      >
        {category && <p className="mb-4 text-sm text-muted-foreground">{category}</p>}
        <div className="space-y-6">
          {order
            // Ohne Lernende braucht es keinen leeren Abschnitt „Wollen das auch lernen“
            .filter(role => role === 'offer' || counts.seek > 0)
            .map(role => (
              <PeopleSection
                key={`${skill.id}-${role}`}
                skill={skill}
                role={role}
                count={counts[role]}
                loader={loader}
                onOpenPerson={openPerson}
                registerRemove={registerRemove}
              />
            ))}
        </div>
      </Modal>
      {person && (
        <ProfileSheet
          open
          onClose={() => setPerson(null)}
          userId={person.userId}
          name={person.name}
          origin="SKILL_BROWSE"
          onBlocked={handleBlocked}
        />
      )}
    </>
  );
};

SkillDetailSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  skill: skillShape,
  category: PropTypes.string,
  /** loader(skillId, role, cursor) → { people, nextCursor, total }; Standard: API */
  loader: PropTypes.func,
  trackEvents: PropTypes.bool,
};

export default SkillDetailSheet;
